import { h, Component } from 'preact';
import { commaSeparatedList, joinClasses, sortObjectByOrder } from 'utils/utils';
import { getCopy } from 'utils/copy';
import { Randomiser } from 'components/bolt/icons/quizPhotoboothIcons';
import Store from 'store/store';
import HTML from 'components/core/html/html';
import s from 'components/bolt/quizPhotobooth/intro.sss';
import { containsProfanity } from 'utils/bolt/profanities';

export default class Intro extends Component {
	constructor(props) {
		super(props);
		this.onIntroInput = this.onIntroInput.bind(this);
		this.start = this.start.bind(this);
		this.onTermsClick = this.onTermsClick.bind(this);
		this.showIntro = this.showIntro.bind(this);
		this.checkIfInputsAreFilled = this.checkIfInputsAreFilled.bind(this);
		this.resetIntro = this.resetIntro.bind(this);
		this.state = {
			termsChecked: false,
			profanity: false,
			landingActive: !!this.props?.intro?.landingPage,
			inputs: this.getInputs(props),
			focusedInput: null,
		};
	}

	componentDidMount() {
		Store.on('QUIZPHOTOBOOTH_INTRO_RESET', this.resetIntro);
		this.resetValues();
		if (this.state.landingActive) {
			Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'landing');
		} else {
			Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'intro');
		}
	}

	componentWillUnmount() {
		Store.off('QUIZPHOTOBOOTH_INTRO_RESET', this.resetIntro);
	}

	getInputs(props = this.props) {
		const inputs = JSON.parse(JSON.stringify(props?.intro?.inputs || {}));
		return sortObjectByOrder(inputs, (input) => {
			if (input.type === 'dropdown' || input.type === 'randomizer') {
				input.options = commaSeparatedList(input.options);
			}
			return input;
		});
	}

	onIntroInput(e) {
		const input = e.target;
		if (input.type.includes('select')) {
			if (input.name === 'location') {
				Store.emit('TRACK_QUIZPHOTOBOOTH_LOCATION_CLICK');
			}
		}
		this.props.setInput({ [input.name]: input.type === 'checkbox' ? input.checked : input.value });
	}

	resetValues() {
		const { showTermsAndConditions } = this.props.intro || {};
		if (showTermsAndConditions) {
			this.setState({ termsChecked: false });
		}
		this.props.setInput(Object.fromEntries(this.state.inputs.map((input) => [input.contentKey, input.options ? input.options[0] : ''])));
	}

	checkIfInputsAreFilled() {
		return this.state.inputs.every(input => input.type === 'dropdown' || input.optional || this.props.inputValues?.[input.contentKey]);
	}

	start(e) {
		e.preventDefault();
		if (this.state.started) {
			return false;
		}
		const { termsChecked } = this.state;
		const { showTermsAndConditions } = this.props.intro || {};
		const values = this.props.inputValues;
		const inputsFilled = this.checkIfInputsAreFilled();
		const profanity = this.state.inputs.some((input) => input.type === 'text' && containsProfanity(values[input.contentKey]));
		if (!inputsFilled || profanity || (showTermsAndConditions && !termsChecked)) {
			return false;
		}
		this.setState({ started: true });
		Store.emit('TRACK_QUIZPHOTOBOOTH_START_CLICK');
		this.props.advanceStep();
		return true;
	}

	onTermsClick(e) {
		const termsChecked = e?.target ? e.target.checked : !this.state.termsChecked;
		Store.emit('TRACK_QUIZPHOTOBOOTH_TERMS_CLICK', termsChecked);
		this.setState({ termsChecked });
	}

	showIntro() {
		if (this.state.landingActive) {
			Store.emit('TRACK_QUIZPHOTOBOOTH_VIEW', 'intro');
		}
		this.setState({ landingActive: false });
		Store.emit('TRACK_QUIZPHOTOBOOTH_INTRO_START');
	}

	generateRandomValue(options, name) {
		// Only return if random value doesn't match current input value, if it does, rerun.
		const current = this.props?.inputValues[name];
		const newOptions = options.filter((option) => option !== current);
		return newOptions[Math.floor(Math.random() * newOptions.length)];
	}

	handleRandomize(options, name) {
		const randomValue = this.generateRandomValue(options, name);
		Store.emit('TRACK_QUIZPHOTOBOOTH_RANDOMIZER_CLICK');
		this.props.setInput({ [name]: randomValue });
	}

	resetIntro() {
		if (!this.props?.intro?.landingPage) return;
		this.setState({ landingActive: true });
	}

	handleInputFocus(e) {
		const input = e.target;
		Store.emit('TRACK_QUIZPHOTOBOOTH_INPUT_CLICK', input.name);
		if (input.name === 'name') {
			Store.emit('TRACK_QUIZPHOTOBOOTH_NAME_CLICK');
		}
		if (input.name === 'skill') {
			Store.emit('TRACK_QUIZPHOTOBOOTH_RANDOMIZER_CLICK', input.value);
		}
		this.setState({ focusedInput: input.name });
	}

	handleInputFocusOut() {
		this.setState({ focusedInput: null });
	}

	renderInputText(input) {
		const value = this.props.inputValues?.[input.contentKey];
		const profanity = containsProfanity(value);
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		const profanityText = this.props.appData?.copy?.baseCopy?.$QUIZPHOTOBOOTH_PROFANITY_ERROR || this.props.appData?.options?.profanities?.warning || 'Please Try Again.';
		const isFocused = this.state.focusedInput === input.contentKey;
		return [
			<input
				type={input.inputType || 'text'}
				class={joinClasses(s.nameInput, fontStyles.quizPhotoboothOption)}
				name={input.contentKey}
				value={value}
				placeholder={input.placeholder || ''}
				autocomplete={input.autocomplete}
				onFocus={(e) => this.handleInputFocus(e)}
				onFocusOut={(e) => this.handleInputFocusOut(e)}
				onInput={this.onIntroInput}
				maxLength={input.maxLength || this.props.intro?.inputMaxLength}
				required={!input.optional}
				key="input-text"
			/>,
			<HTML class={joinClasses(s.profanityError, fontStyles.quizPhotoboothInfo, profanity && !isFocused && s.show)} content={profanityText} markdown key="profanity" />
		];
	}

	renderInputDropdown(input) {
		const value = this.props.inputValues?.[input.contentKey];
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		return (
			<select
				name={input.contentKey}
				value={value}
				onFocus={(e) => Store.emit('TRACK_QUIZPHOTOBOOTH_INPUT_CLICK', e.target.name)}
				onChange={this.onIntroInput}
				class={joinClasses(s.nameInput, s.nameSelect, fontStyles.quizPhotoboothOption)}
				key="input-dropdown"
			>
				{input.options.map((option) => <option value={option} class={s.introOption}>{option}</option>)}
			</select>
		);
	}

	renderInputRandomizer(input) {
		const value = this.props.inputValues?.[input.contentKey];
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		return (
			<div class={joinClasses(s.nameInput, s.randomiser, fontStyles.quizPhotoboothOption)} key="randomizer" name={input.contentKey}>
				<div>{value}</div>
				<button type="button" class={joinClasses(s.randomiserButton, fontStyles.quizPhotoboothButton)} onClick={() => this.handleRandomize(input.options, input.contentKey)}>
					<div>Shuffle</div>
					<Randomiser class={s.randomiserIcon} />
				</button>
			</div>
		);
	}

	renderInputContent(input) {
		return {
			text: this.renderInputText,
			dropdown: this.renderInputDropdown,
			randomizer: this.renderInputRandomizer,
		}[input.type]?.call(this, input);
	}

	renderInputs() {
		const fontStyles = this.props.appData?.styling?.fontVariables || {};
		return (
			<div class={s.inputs}>
				{this.state.inputs.map((input) => {
					const Tag = input.wrapperTag || 'label';
					return (
						<Tag class={joinClasses(s.input, s[`inputWrapper-type-${input.type}`], s[`inputWrapper-${input.contentKey}`])}>
							{!!input.text && <HTML class={joinClasses(s.introCopy, fontStyles.quizPhotoboothOption)} content={input.text} markdown key="intro" />}
							{this.renderInputContent(input)}
						</Tag>
					);
				})}
			</div>
		);
	}

	renderIntro() {
		const { isDesktopWidth, assets, inputValues, appData } = this.props;
		const { termsChecked } = this.state;
		const intro = this.props.intro || {};
		const { showTermsAndConditions } = intro;
		const fontStyles = appData?.styling?.fontVariables || {};
		const copy = appData?.copy?.baseCopy || {};
		const termsAreUnchecked = showTermsAndConditions && !termsChecked;

		const backgroundImage = isDesktopWidth ? intro.introBackgroundDesktop : intro.introBackgroundMobile;
		const isBackgroundImageVideo = backgroundImage?.endsWith('.mp4') || backgroundImage?.endsWith('.webm');
		const introBackgroundStyle = !isBackgroundImageVideo && backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {};
		const introImage = isDesktopWidth ? this.props.intro.introImage : (this.props.intro.introImageMobile || this.props.intro.introImage);
		const isIntroImageVideo = introImage?.endsWith('.mp4') || introImage?.endsWith('.webm');

		const values = this.props.inputValues;
		const profanity = this.state.inputs.some((input) => input.type === 'text' && containsProfanity(values[input.contentKey]));
		const allowStart = this.checkIfInputsAreFilled() && !profanity && !termsAreUnchecked;

		return (
			<div class={joinClasses(s.section, !this.state.landingActive && s.active)} style={introBackgroundStyle}>
				{!!backgroundImage && isBackgroundImageVideo && <video class={s.video} src={backgroundImage} autoplay loop muted playsinline />}
				<form class={s.introContent} onSubmit={this.start} name={intro.formName}>
					{!!introImage && isIntroImageVideo ? (
						<video class={joinClasses(s.video, s.introImage)} src={introImage} autoplay loop muted playsinline />
					) : (
						<img src={introImage} class={s.introImage} alt={intro.introImgAltText} />
					)}
					<HTML class={joinClasses(s.introCopy, fontStyles.quizPhotoboothCopy)} content={copy.$QUIZPHOTOBOOTH_INTRO_COPY} markdown />
					{inputValues && this.renderInputs()}
					{showTermsAndConditions && (
						<label class={joinClasses(s.termsConditions, fontStyles.quizPhotoboothInfo)}>
							<input type="checkbox" checked={termsChecked} onChange={this.onTermsClick} required />
							<HTML content={getCopy('QUIZPHOTOBOOTH_LEGAL_CHECKBOX')} markdown />
						</label>
					)}
					<button type="submit" class={joinClasses(s.startButton, allowStart && s.active)}>
						<HTML tag="span" class={fontStyles.quizPhotoboothButton} content={copy.$QUIZPHOTOBOOTH_START} markdown inline />
					</button>
				</form>
				<img src={assets.bottomImage} class={s.bottomImage} alt={assets.bottomImageAltText || ''} />
			</div>
		);
	}

	renderLanding() {
		const { backgroundDesktop, backgroundMobile, heroImageDesktop, heroImageMobile, landingText, ctaButtonText, heroAltText } = this.props.intro?.landingPage || {};
		const bottomImage = this.props.assets.bottomImage;
		const bottomImageAltText = this.props.assets.bottomImageAltText || '';
		const isDesktopWidth = this.props.isDesktopWidth;
		const fontStyles = this.props.appData?.styling?.fontVariables || {};

		const backgroundImage = isDesktopWidth ? backgroundDesktop : backgroundMobile;
		const isVideo = backgroundImage?.endsWith('.mp4') || backgroundImage?.endsWith('.webm');
		const landingBackgroundStyle = !isVideo && backgroundImage ? { backgroundImage: `url(${backgroundImage})` } : {};

		const heroImage = isDesktopWidth ? heroImageDesktop : heroImageMobile;
		const heroImageIsVideo = heroImage?.endsWith('.mp4') || heroImage?.endsWith('.webm');


		return (
			<div class={joinClasses(s.section, this.state.landingActive && s.active)} style={landingBackgroundStyle}>
				{!!backgroundImage && isVideo && <video ref={($ref) => this.$landingVideo = $ref} class={s.video} src={backgroundImage} autoplay loop muted playsinline />}
				<div class={s.landingContainer}>
					{!!heroImage && !heroImageIsVideo ? (
						<img src={heroImage} class={s.landingHero} alt={heroAltText} />
					) : (
						<video class={joinClasses(s.video, s.landingHero)} src={heroImage} autoplay loop muted playsinline />
					)}
					<HTML class={joinClasses(s.introCopy, s.landingCopy, isDesktopWidth ? fontStyles.quizPhotoboothMessage : fontStyles.quizPhotoboothCopy)} content={landingText} markdown />
					<button class={joinClasses(s.startButton, s.landingButton, s.active)} onClick={this.showIntro}>
						<span class={fontStyles.quizPhotoboothButton}>{ctaButtonText}</span>
					</button>
				</div>
				<img src={bottomImage} class={s.bottomImage} alt={bottomImageAltText} />
			</div>
		);
	}

	render({ intro, transition }) {
		return (
			<div class={joinClasses(s.container, s[`transition-${transition}`])}>
				{intro?.landingPage && this.renderLanding()}
				{this.renderIntro()}
			</div>
		);
	}
}